import { useCallback, useEffect, useState } from "react";

export interface FavoriteItem {
  id: string;
  type: "gallery" | "photobook";
  title: string;
  cover: string;
  link: string;
}

const STORAGE_KEY = "bm_favorites";
const CHANGE_EVENT = "bm-favorites-changed";

function readFavorites(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FavoriteItem[]) : [];
  } catch {
    return [];
  }
}

function writeFavorites(items: FavoriteItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // ignore storage errors (e.g. private mode)
  }
}

export function useFavorites() {
  const [items, setItems] = useState<FavoriteItem[]>(() => readFavorites());

  useEffect(() => {
    const handler = () => setItems(readFavorites());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const isFavorite = useCallback(
    (id: string) => items.some((item) => item.id === id),
    [items]
  );

  const toggleFavorite = useCallback((item: FavoriteItem) => {
    const current = readFavorites();
    const exists = current.some((entry) => entry.id === item.id);
    if (exists) {
      writeFavorites(current.filter((entry) => entry.id !== item.id));
    } else {
      writeFavorites([item, ...current]);
    }
  }, []);

  const removeFavorite = useCallback((id: string) => {
    writeFavorites(readFavorites().filter((entry) => entry.id !== id));
  }, []);

  const clearFavorites = useCallback(() => {
    writeFavorites([]);
  }, []);

  return { items, isFavorite, toggleFavorite, removeFavorite, clearFavorites };
}